
import { supabase } from "@/integrations/supabase/client";
import { ShoppingItem, ShoppingItemWithProduct, Category, UserProduct } from "@/types";
import { ProductAttribute } from "@/types/attributes";
import OfflineLogger from '@/utils/offlineLogger';
import { mapItemFromDatabase, SHOPPING_ITEM_COLUMNS, USER_PRODUCT_COLUMNS } from "../utils";
import { isValidUUID } from "@/utils/validation";
import { getMaxPosition } from "@/services/shopping/lists/offline-position.service";
import { ensureCategoryInList } from "@/services/shopping/offline-categories-in-list.service";
import { networkService } from '@/services/network.service';
import { shoppingItemRepository } from "@/repositories/shoppingItem.repository";
import { useOfflineQuery } from "@/hooks/useOfflineQuery";
import { queryClient } from '@/services/queryClient';
import { offlineDb } from '@/db/offlineDb';
import { getCachedUserId, setCachedUser } from '@/services/auth/sessionCache';
import { getValidUserId } from '@/utils/validation';
import { categoryOperationsService } from '@/services/categories/categoryOperations.service';
import { validateQuantity } from '@/utils/security';

const DEFAULT_SANITIZED_QUANTITY = (() => {
  const result = validateQuantity('1');
  return result.isValid ? result.sanitized : '1';
})();

const normalizeQuantityValue = (value?: string | number | null) => {
  const result = validateQuantity(value ?? DEFAULT_SANITIZED_QUANTITY);
  if (result.isValid) {
    return result.sanitized;
  }
  return DEFAULT_SANITIZED_QUANTITY;
};

/**
 * Trova un elemento esistente nella lista in base all'ID del prodotto
 */
export const findExistingItemInList = async (
  listId: string,
  productId: string,
  onlyChecked: boolean = false
): Promise<ShoppingItem | null> => {
  try {
    OfflineLogger.info('findExistingItemInList', `Cercando prodotto ${productId} nella lista ${listId} (onlyChecked: ${onlyChecked})`);
    
    if (networkService.isOnline()) {
      // 🟢 ONLINE: cerca in Supabase
      let query = supabase
        .from("shopping_items")
        .select("*")
        .eq("list_id", listId)
        .eq("product_id", productId);
        
      // Aggiungi filtro checked se necessario
      if (onlyChecked) {
        query = query.eq("checked", true);
      }
        
      const { data, error } = await query.limit(1);
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        OfflineLogger.info('findExistingItemInList', `Trovato prodotto esistente con ID: ${data[0].id}`);
        return data[0] as ShoppingItem;
      }
      
      return null;
    } else {
      // 🔴 OFFLINE: cerca localmente
      const localItems = await shoppingItemRepository.getByListId(listId);
      const existingItem = localItems.find(item => 
        item.product_id === productId && 
        (!onlyChecked || item.checked === true)
      );
      
      if (existingItem) {
        OfflineLogger.info('findExistingItemInList', `Trovato prodotto esistente offline: ${existingItem.id}`);
      } else {
        OfflineLogger.info('findExistingItemInList', `Nessun prodotto esistente trovato offline`);
      }
      
      return existingItem || null;
    }
  } catch (err) {
    OfflineLogger.error('findExistingItemInList', `Error: ${err}`);
    return null;
  }
};

// Function to add an item to a shopping list
export const addShoppingItem = async (
  listId: string, 
  item: Omit<ShoppingItem, "id" | "checked" | "created_at" | "user_id" | "list_id">
): Promise<ShoppingItem | null> => {
  try {
    OfflineLogger.info('addShoppingItem', `Adding item to list ${listId}`);
    
    let userId = await getCachedUserId();
    if (!userId) {
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        userId = userData.user.id;
        setCachedUser(userData.user);
      } else {
        userId = getValidUserId() ?? undefined;
      }
    }

    let categoryValue = null;
    if (!item.category || !isValidUUID(item.category)) {
      const other = await categoryOperationsService.getOrCreateCategory('Altro');
      categoryValue = other.id;
    } else if (item.category !== 'none') {
      categoryValue = item.category;
    }

    // ✅ Assicura che la categoria sia registrata in shopping_categories
    if (categoryValue) {
      await ensureCategoryInList(listId, categoryValue);
    }

    // 1. Prima verifica se esiste un elemento già acquistato con lo stesso product_id
    const existingCheckedItem = await findExistingItemInList(listId, item.product_id, true);
    
    // 2. Se esiste un elemento già acquistato, lo riattiva invece di crearne uno nuovo
    if (existingCheckedItem) {
      OfflineLogger.info('addShoppingItem', `Riattivazione item esistente ${existingCheckedItem.id} invece di crearne uno nuovo`);
      
      // Prepara l'aggiornamento: imposta checked = false, ma mantieni gli attributi esistenti
      const updateData: Partial<ShoppingItem> = {
        checked: false,
      };
      
      // Aggiorna solo se specificati
      if (item.quantity) updateData.quantity = item.quantity;
      if (item.unit) updateData.unit = item.unit;
      if (item.category) updateData.category = categoryValue ?? null;
      if (item.attributes) updateData.attributes = item.attributes;
      
      // Usa il repository per il supporto offline
      await shoppingItemRepository.update(existingCheckedItem.id, updateData);

      // Se siamo online, sincronizza subito l'operazione in coda
      if (networkService.isOnline()) {
        try {
          const { syncService } = await import('@/services/sync.service');
          await syncService.syncPendingOperations();
        } catch (syncError) {
          OfflineLogger.error('addShoppingItem.syncPending', syncError);
        }
      }

      // Invalida la cache degli articoli per riflettere la modifica
      await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });

      return {
        ...existingCheckedItem,
        ...updateData
      };
    }
    
    // 3. Verifica se esiste un elemento non acquistato con lo stesso product_id
    const existingUncheckedItem = await findExistingItemInList(listId, item.product_id, false);
    
    if (existingUncheckedItem && !existingUncheckedItem.checked) {
      // Se esiste già un prodotto non acquistato, non fare nulla
      OfflineLogger.info('addShoppingItem', `Il prodotto esiste già come non acquistato, ID: ${existingUncheckedItem.id}`);
      return existingUncheckedItem;
    }

    // Imposta la posizione per il nuovo elemento usando il servizio offline
    let position = 0;
    try {
      const maxPosition = await getMaxPosition(listId);
      position = maxPosition !== null ? maxPosition + 1 : 0;
      OfflineLogger.info('addShoppingItem', `Setting position: ${position} (max was: ${maxPosition})`);
    } catch (err) {
      OfflineLogger.warn('addShoppingItem', `Couldn't get max position, defaulting to 0: ${err}`);
    }

    // Create a new item object with explicitly typed properties
    const newItem: ShoppingItem = {
      id: '', // Will be set by the repository
      list_id: listId,
      product_id: item.product_id,
      quantity: item.quantity,
      unit: item.unit,
      category: categoryValue ?? null,
      checked: false,
      position: position,
      created_at: new Date(),
      user_id: userId || undefined,
      attributes: item.attributes || []
    };

    // 🔀 Write path unico (#1426): online e offline passano entrambi dal
    // repository, che scrive in Dexie in ottimistico e accoda l'operazione di
    // sync. Il `id` generato dal client viene poi usato come id sul server
    // (vedi syncCreate → insert con id esplicito), quindi il valore di ritorno
    // resta stabile per i chiamanti che lo riusano subito (es. updateItemStatus).
    const createdItem = await shoppingItemRepository.create(newItem, listId);
    OfflineLogger.info('addShoppingItem', `Item created via repository with ID ${createdItem.id}`);

    // Online: spingi subito la coda così la scrittura raggiunge il server senza
    // attendere il ciclo periodico (stesso pattern del ramo di riattivazione).
    if (networkService.isOnline()) {
      try {
        const { syncService } = await import('@/services/sync.service');
        await syncService.syncPendingOperations();
      } catch (syncError) {
        OfflineLogger.error('addShoppingItem.syncPending', syncError);
      }
    }

    return createdItem;
  } catch (err) {
    // Con il write path unico la create passa già dal repository (Dexie
    // ottimistico) sia online che offline: non c'è più un fallback distinto da
    // tentare qui, una nuova create ripeterebbe lo stesso percorso fallito.
    OfflineLogger.error('addShoppingItem', `Error: ${err}`);
    return null;
  }
};

// Recupera gli articoli per una lista, ordinati per posizione
export const fetchShoppingItems = async (
  listId: string
): Promise<ShoppingItemWithProduct[]> => {
  OfflineLogger.info("fetchShoppingItems", `Fetching items for list ${listId}`);
  
  if (!listId) return [];

  try {
    if (networkService.isOnline()) {
      // 🟢 ONLINE: prendi da Supabase con join prodotti
      const { data, error } = await supabase
        .from("shopping_items")
        .select(
          `${SHOPPING_ITEM_COLUMNS}, user_products!shopping_items_product_user_fk(${USER_PRODUCT_COLUMNS})`
        )
        .eq("list_id", listId);

      if (error) {
        throw error;
      }

      if (data) {
        OfflineLogger.info(
          "fetchShoppingItems",
          `Fetched ${data.length} items from Supabase for list ${listId}`
        );

        const mapped: ShoppingItemWithProduct[] = [];

        for (const row of data as any[]) {
          const item = mapItemFromDatabase(row);
          const prod = row.user_products
            ? {
                id: row.user_products.id,
                userId: row.user_products.user_id,
                name: row.user_products.name,
                defaultQuantity: row.user_products.default_quantity,
                defaultUnit: row.user_products.default_unit,
                photo: row.user_products.photo,
                barcode: row.user_products.barcode,
                openfoodfacts_id: row.user_products.openfoodfacts_id,
                categoryId: row.user_products.category_id,
                createdAt: row.user_products.created_at,
                attributes: row.user_products.attributes || [],
              }
            : undefined;

          if (!prod && item.product_id) {
            OfflineLogger.debug(
              'fetchShoppingItems',
              `Missing user_product, trying fallback for item ${item.id} (product ${item.product_id})`
            );
            const fallback = await getProductForShoppingItem(item.product_id);
            if (fallback) {
              OfflineLogger.debug('fetchShoppingItems', `Fallback OK for ${item.id}`);
              mapped.push({ ...item, product: fallback });
              continue;
            }
            OfflineLogger.warn('fetchShoppingItems', `Fallback failed for ${item.id}`);
          }

          mapped.push({ ...item, product: prod });
        }

        // Salva in cache locale per uso offline
        await shoppingItemRepository.saveMany(mapped);

        return mapped;
      }

      return [];
    } else {
      // 🔴 OFFLINE: fallback su dati locali
      OfflineLogger.info(
        "fetchShoppingItems",
        `Offline: retrieving local items for list ${listId}`
      );
      return await shoppingItemRepository.getWithProductsByListId(listId);
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : JSON.stringify(error);
    OfflineLogger.error(
      "fetchShoppingItems",
      `Errore nel fetch. Provo fallback locale: ${errorMessage}`
    );
    // Fallback in caso di errore anche se online
    try {
      return await shoppingItemRepository.getWithProductsByListId(listId);
    } catch (localError) {
      OfflineLogger.error("fetchShoppingItems.localFallback", localError);
      return [];
    }
  }
};

export const useShoppingItems = (listId: string) => {
  return useOfflineQuery<ShoppingItemWithProduct[]>(
    ["shopping-items", listId],
    () => fetchShoppingItems(listId),
    {
      enabled: !!listId,
    }
  );
};

// Function to update an item's checked status
export const updateItemStatus = async (itemId: string, checked: boolean, userId?: string): Promise<boolean> => {
  try {
    OfflineLogger.info(`updateItemStatus`, `Updating item ${itemId} to checked: ${checked}`);
    let listId: string | null = null;
    let success = false;

    const localItemRecord = await offlineDb.shoppingItems.get(itemId);
    const sanitizedQuantity = normalizeQuantityValue(localItemRecord?.quantity ?? null);

    if (networkService.isOnline()) {
      try {
        const { data, error } = await supabase
          .from('shopping_items')
          .update({ checked, quantity: sanitizedQuantity })
          .eq('id', itemId)
          .select()
          .single();

        if (error) {
          throw error;
        }

        if (data) {
          const updated = mapItemFromDatabase(data);
          listId = updated.list_id;
          await offlineDb.shoppingItems.put({
            ...updated,
            quantity: sanitizedQuantity,
            // #1421: categoria vuota → null nello storage, coerente con Supabase
            category: updated.category || null,
            created_at: updated.created_at instanceof Date
              ? updated.created_at.toISOString()
              : new Date(updated.created_at).toISOString(),
            attributes: updated.attributes || [],
            _syncStatus: 'synced' as const,
            _lastModified: Date.now()
          });
          success = true;
        }
      } catch (supError) {
        OfflineLogger.error('updateItemStatus.supabase', supError);
      }
    }

    if (!success) {
      success = await shoppingItemRepository.updateItemStatus(itemId, checked);
      const localItem = await offlineDb.shoppingItems.get(itemId);
      listId = localItem?.list_id || listId;
    }

    if (success && listId) {
      await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
    }

    if (success) {
      OfflineLogger.info(`updateItemStatus`, `Successfully updated item ${itemId}`);
    } else {
      OfflineLogger.error(`updateItemStatus`, `Failed to update item ${itemId}`);
    }

    return success;
  } catch (err) {
    OfflineLogger.error(`updateItemStatus`, `Error for item ${itemId}: ${err}`);
    return false;
  }
};

// Function to update an item's details
export const updateShoppingItem = async (
  itemId: string,
  updates: Partial<{
    quantity: string;
    unit: string;
    category: Category;
    product_id: string;
    attributes: ProductAttribute[];
  }>
): Promise<boolean> => {
  try {
    OfflineLogger.info(`updateShoppingItem`, `Updating item ${itemId}`);
    
    // Validate category if provided in updates
    if (updates.category && updates.category !== 'none') {
      if (!isValidUUID(updates.category)) {
        OfflineLogger.info(`updateShoppingItem`, `Non-UUID category in update: ${updates.category}`);
      }
    }
    
    let listId: string | null = null;
    let success = false;

    if (networkService.isOnline()) {
      try {
        const { data, error } = await supabase
          .from('shopping_items')
          .update({
            quantity: updates.quantity,
            unit: updates.unit,
            category: updates.category,
            product_id: updates.product_id,
            attributes: updates.attributes,
          })
          .eq('id', itemId)
          .select()
          .single();

        if (error) {
          throw error;
        }

        if (data) {
          const updated = mapItemFromDatabase(data);
          listId = updated.list_id;
          await offlineDb.shoppingItems.put({
            ...updated,
            // #1421: categoria vuota → null nello storage, coerente con Supabase
            category: updated.category || null,
            created_at: updated.created_at instanceof Date
              ? updated.created_at.toISOString()
              : new Date(updated.created_at).toISOString(),
            attributes: updated.attributes || [],
            _syncStatus: 'synced' as const,
            _lastModified: Date.now(),
          });
          success = true;
        }
      } catch (supError) {
        OfflineLogger.error('updateShoppingItem.supabase', supError);
      }
    }

    if (!success) {
      const updatedItem = await shoppingItemRepository.update(itemId, updates as Partial<ShoppingItem>);
      success = !!updatedItem;
      const localItem = await offlineDb.shoppingItems.get(itemId);
      listId = localItem?.list_id || listId;
    }

    if (success && listId) {
      await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
    }

    if (success) {
      OfflineLogger.info(`updateShoppingItem`, `Successfully updated item ${itemId}`);
    } else {
      OfflineLogger.error(`updateShoppingItem`, `Failed to update item ${itemId}`);
    }

    return success;
  } catch (err) {
    OfflineLogger.error(`updateShoppingItem`, `Error updating item ${itemId}: ${err}`);
    return false;
  }
};

// Function to delete a shopping item
export const deleteShoppingItem = async (itemId: string): Promise<boolean> => {
  try {
    OfflineLogger.info(`deleteShoppingItem`, `Deleting item ${itemId}`);
    let listId: string | null = null;
    let success = false;

    if (networkService.isOnline()) {
      try {
        const { data, error } = await supabase
          .from('shopping_items')
          .delete()
          .eq('id', itemId)
          .select()
          .maybeSingle();

        if (error) {
          throw error;
        }

        listId = (data as any)?.list_id || null;
        await offlineDb.shoppingItems.delete(itemId);
        success = true;
      } catch (supError) {
        OfflineLogger.error('deleteShoppingItem.supabase', supError);
      }
    }

    if (!success) {
      await shoppingItemRepository.delete(itemId);
      const localItem = await offlineDb.shoppingItems.get(itemId);
      listId = localItem?.list_id || listId;
      success = true;
    }

    if (listId) {
      await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
    }

    OfflineLogger.info(`deleteShoppingItem`, `Successfully deleted item ${itemId}`);
    return true;
  } catch (error) {
    OfflineLogger.error(`deleteShoppingItem`, `Error deleting item ${itemId}: ${error}`);
    return false;
  }
};

// Fetch product information for a shopping item
export const getProductForShoppingItem = async (productId: string): Promise<UserProduct | null> => {
  if (!productId) return null;
  
  try {
    OfflineLogger.info(`getProductForShoppingItem`, `Fetching product ${productId}`);
    
    // First check local cache
    try {
      const localData = localStorage.getItem(`product-${productId}`);
      if (localData) {
        OfflineLogger.info(`getProductForShoppingItem`, `Found product ${productId} in local cache`);
        return JSON.parse(localData);
      }
    } catch (cacheError) {
      OfflineLogger.error(`getProductForShoppingItem`, `Error reading cache for ${productId}: ${cacheError}`);
    }
    
    // If online, fetch from remote
    if (networkService.isOnline()) {
      try {
        const { data, error } = await supabase
          .from('user_products')
          .select('*')
          .eq('id', productId)
          .single();

        if (error) {
          OfflineLogger.error(
            'getProductForShoppingItem',
            `Supabase error for ${productId}:`,
            error
          );
          return null;
        }

        // Save to local cache
        if (data) {
          try {
            localStorage.setItem(`product-${productId}`, JSON.stringify(data));
            OfflineLogger.info(`getProductForShoppingItem`, `Saved product ${productId} to local cache`);
          } catch (cacheError) {
            OfflineLogger.error(`getProductForShoppingItem`, `Error saving to cache: ${cacheError}`);
          }
          return data as UserProduct;
        }
      } catch (remoteError) {
        OfflineLogger.error(`getProductForShoppingItem`, `Remote error for ${productId}: ${remoteError}`);
        return null;
      }
    } else {
      OfflineLogger.info(`getProductForShoppingItem`, `Offline, no cached data for product ${productId}`);
      return null;
    }
  } catch (err) {
    OfflineLogger.error(`getProductForShoppingItem`, `General error for ${productId}: ${err}`);
    return null;
  }

  OfflineLogger.warn('getProductForShoppingItem', `No data for ${productId}`);
  return null;
};

// Helper function to get item name from product_id
export const getItemName = async (item: ShoppingItem): Promise<string> => {
  if (!item.product_id) return "Unknown Item";
  
  try {
    const product = await getProductForShoppingItem(item.product_id);
    return product?.name || "Unknown Item";
  } catch (error) {
    OfflineLogger.error(`getItemName`, `Error for ${item.id}: ${error}`);
    return "Unknown Item";
  }
};
